import { keywordsMeta } from "@/lib/seo/keywords";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { ContactForm } from "@/components/site/ContactForm";
import { CallbackBar } from "@/components/site/CallbackBar";
import { TeamStrip } from "@/components/site/TeamStrip";
import { Clock, Film, Mail, Phone, Sparkles, Users } from "lucide-react";
import { useState } from "react";
import { track } from "@/lib/analytics";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { aeoGeo, breadcrumbs, localBusinessForCity } from "@/lib/seo/schema";
import { CONTACT } from "@/content/contact";

const KONTAKT_FAQ = [
  {
    q: "Kostar mötet något?",
    a: "Nej. 30 minuter där vi går igenom var ni är och om vi kan hjälpa. Säger ni eller vi nej är vi inte sura.",
  },
  {
    q: "Hur snabbt får vi svar?",
    a: "Inom en arbetsdag, oftast samma dag. Behöver ni svar snabbare — ring 08 480 04 706.",
  },
  {
    q: "Vad ska vi förbereda?",
    a: "Inget. Det räcker med att ni har en grov bild av vad ni vill åstadkomma. Vi ställer frågorna.",
  },
  {
    q: "Vem dyker upp på mötet?",
    a: "En av grundarna plus produktionsledare. Alltid människor som faktiskt kan göra det vi pratar om — aldrig en säljare.",
  },
  {
    q: "Kan vi söka jobb eller frilansa hos er?",
    a: "Ja. Skicka in via karriärsidan så hamnar ansökan direkt hos oss — vi läser alla spontanansökningar.",
  },
];

type Track = "film" | "annat";
type FilmIntent = "pulse" | "prime" | "podd" | "live" | "post" | "annat";

export const Route = createFileRoute("/kontakt")({
  // Paused photo deep links no longer select a photo form.
  validateSearch: (
    search: Record<string, unknown>,
  ): { omrade?: "film" | "annat"; intent?: FilmIntent } => {
    const omrade = String(search["omrade"] ?? "");
    const intent = String(search["intent"] ?? "");
    const out: { omrade?: "film" | "annat"; intent?: FilmIntent } = {};
    if (omrade === "film" || omrade === "annat") out.omrade = omrade;
    if (["pulse", "prime", "podd", "live", "post", "annat"].includes(intent)) {
      out.intent = intent as FilmIntent;
    }
    return out;
  },
  head: () => ({
    meta: [
      ...keywordsMeta("/kontakt"),
      { title: "Kontakta Fenny — film och offert inom en arbetsdag" },
      { name: "description", content: "Kontakta Fenny om film eller post-produktion. Välj ärende, fyll i formuläret och få svar inom en arbetsdag. Kostnadsfritt strategimöte, fast pris." },
      { property: "og:title", content: "Kontakta Fenny — film och offert inom en arbetsdag" },
      { property: "og:description", content: "Välj ärende — film eller något annat. Vi svarar inom en arbetsdag." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/kontakt" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Kontakta Fenny — film och offert inom en arbetsdag" },
      { name: "twitter:description", content: "Välj ärende — film eller något annat. Vi svarar inom en arbetsdag." },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/kontakt" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/kontakt", name: "Kontakta Fenny — film och offert inom en arbetsdag", description: "Kontakta Fenny om film eller post-produktion. Välj ärende, fyll i formuläret och få svar inom en arbetsdag."})) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Kontakt", path: "/kontakt" }])) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Stockholm", path: "/kontakt", postalRegion: "Stockholms län", bookingPath: "/kontakt", description: "Kontakta Fenny för film och post-produktion. Kostnadsfritt strategimöte, fast pris innan start och svar inom en arbetsdag." })) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: KONTAKT_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),

  component: KontaktPage,
});

const TRACKS: {
  id: Track;
  label: string;
  icon: typeof Film;
  blurb: string;
}[] = [
  { id: "film", label: "Film & rörligt", icon: Film, blurb: "Abonnemang, kampanjfilm, event, podd" },
  { id: "annat", label: "Något annat", icon: Sparkles, blurb: "Post-produktion, samarbete, press" },
];

const FILM_INTENTS: { value: FilmIntent; label: string }[] = [
  { value: "pulse", label: "Löpande film (Pulse)" },
  { value: "prime", label: "Större produktion" },
  { value: "podd", label: "Podcast" },
  { value: "live", label: "Live / event" },
  { value: "post", label: "Post-produktion" },
  { value: "annat", label: "Vet inte än" },
];

const STEPS = [
  { icon: Clock, title: "1. Ni skickar ärendet", body: "Två minuter i formuläret. Ingen registrering, ingen chatbot." },
  { icon: Users, title: "2. Vi hör av oss inom en arbetsdag", body: "Rätt person svarar direkt — produktionsledare för ert ärende." },
  { icon: Sparkles, title: "3. Ni får ett konkret upplägg", body: "Förslag på format, tempo och fast pris. Inga dolda påslag, ingen bindningspress." },
];

function KontaktPage() {
  const { omrade, intent } = Route.useSearch();
  const [activeTrack, setActiveTrack] = useState<Track>(omrade ?? "film");
  const [filmIntent, setFilmIntent] = useState<FilmIntent>(intent ?? "annat");

  function selectTrack(id: Track) {
    setActiveTrack(id);
    track("contact_track_select", { location: "kontakt", track: id });
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-10 md:pt-44 md:pb-14">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_70%_0%,color-mix(in_oklab,var(--peach)_18%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="max-w-3xl">
            <LivePulseBadge tone="peach">Vi svarar inom en arbetsdag</LivePulseBadge>
            <h1 className="mt-6 text-balance text-4xl font-semibold leading-[1.05] text-foreground md:text-6xl">
              Berätta vad ni behöver —{" "}
              <em className="font-serif font-normal italic text-peach">vi tar resten.</em>
            </h1>
            <p className="mt-6 max-w-2xl text-pretty text-lg text-muted-foreground">
              Filmproduktion eller något annat? Välj ärende nedan så hamnar ni
              direkt hos rätt person — utan säljled, utan offertspel.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#kontaktformular"
                className="inline-flex items-center gap-2 rounded-full bg-peach px-6 py-3 text-sm font-semibold text-peach-foreground transition-shadow hover:shadow-[0_20px_60px_-12px] hover:shadow-peach/40"
                onClick={() => track("contact_hero_cta", { location: "kontakt" })}
              >
                Till formuläret →
              </a>
              <a
                href="tel:+46848004706"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-foreground/90 transition-colors hover:border-white/35"
              >
                <Phone className="h-4 w-4 text-peach" /> 08 480 04 706
              </a>
              <a
                href={CONTACT.emailMailto}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-foreground/90 transition-colors hover:border-white/35"
              >
                <Mail className="h-4 w-4 text-peach" /> {CONTACT.email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ärendeväljare + formulär */}
      <section id="kontaktformular" className="scroll-mt-24 py-4">
        <div className="container-wide">
          <div className="text-xs uppercase tracking-[0.22em] text-peach">Vad gäller det?</div>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Välj område så visar vi rätt formulär och rätt kontaktperson.
          </p>

          <div
            role="tablist"
            aria-label="Välj ärende"
            className="mt-5 grid gap-3 sm:grid-cols-3"
          >
            {TRACKS.map((t) => {
              const active = activeTrack === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => selectTrack(t.id)}
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors ${
                    active
                      ? "border-peach bg-peach/10"
                      : "border-white/12 bg-surface/50 hover:border-white/30"
                  }`}
                >
                  <span
                    className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${
                      active ? "bg-peach/20 text-peach" : "bg-white/5 text-muted-foreground"
                    }`}
                  >
                    <t.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className={`block text-sm font-semibold ${active ? "text-peach" : ""}`}>
                      {t.label}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {t.blurb}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {activeTrack === "film" && (
            <div className="mt-6 flex flex-wrap gap-2.5">
              {FILM_INTENTS.map((c) => {
                const active = filmIntent === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => {
                      setFilmIntent(c.value);
                      track("contact_quick_select", { location: "kontakt", intent: c.value, label: c.label });
                    }}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "border-peach bg-peach/15 text-peach"
                        : "border-white/15 bg-background/40 text-foreground/80 hover:border-white/35"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-2">
            <ContactForm
              key={activeTrack === "annat" ? "annat" : filmIntent}
              defaultIntent={activeTrack === "annat" ? "annat" : filmIntent}
            />
        </div>
      </section>

      {/* Så går det till */}
      <Reveal>
        <section className="py-12 md:py-16">
          <div className="container-wide">
            <div className="grid gap-4 md:grid-cols-3">
              {STEPS.map((s) => (
                <div key={s.title} className="rounded-3xl border border-white/10 bg-surface/50 p-6">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-mint/15 text-mint">
                    <s.icon className="h-4 w-4" />
                  </span>
                  <div className="mt-4 text-sm font-semibold">{s.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
              Det ni <em>inte</em> får: säljare som jagar er, pitch-deck fullt av
              buzzwords eller luddiga overhead-rader i offerten.
            </p>
          </div>
        </section>
      </Reveal>

      {/* Direkta kanaler */}
      <Reveal>
        <section className="pb-4">
          <div className="container-wide">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <ChannelCard
                icon={Mail}
                label="Maila oss"
                value={CONTACT.email}
                href={CONTACT.emailMailto}
                note="Film och allt däremellan"
              />
              <ChannelCard
                icon={Phone}
                label="Telefon"
                value={CONTACT.phone}
                href={CONTACT.phoneHref}
                note={`Vardagar 09–17 · ${CONTACT.street}, ${CONTACT.city}`}
              />
              <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-peach/10 text-peach">
                  <Users className="h-4 w-4" />
                </span>
                <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
                  Jobb & frilans
                </div>
                <Link to="/karriar" className="mt-1 block text-sm font-medium hover:text-peach">
                  Skicka en ansökan →
                </Link>
                <p className="mt-1 text-xs text-muted-foreground">{CONTACT.citiesShort}</p>
              </div>
            </div>
          </div>
        </section>
      </Reveal>

      {/* Team */}
      <Reveal>
        <section className="pt-12 md:pt-16">
          <div className="container-wide">
            <div className="mx-auto max-w-4xl">
              <TeamStrip
                variant="compact"
                eyebrow="Vem träffar ni?"
                title="Ni pratar med människorna som gör jobbet."
                lede="Inget säljteam mellan oss och er. Samma personer på första samtalet som på inspelningsdagen."
              />
            </div>
          </div>
        </section>
      </Reveal>

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <div className="container-wide">
          <div className="mx-auto max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-peach">Det ni brukar undra</div>
            <h2 className="mt-4 text-balance text-3xl font-semibold md:text-5xl">Vanliga frågor</h2>
            <div className="mt-8 divide-y divide-white/10 rounded-3xl border border-white/10 bg-surface/40 backdrop-blur-md">
              {KONTAKT_FAQ.map((f) => (
                <details key={f.q} className="group p-6 md:p-7">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold">
                    <span>{f.q}</span>
                    <span className="mt-1 text-peach transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-between gap-6 rounded-3xl border border-peach/30 bg-peach/5 p-6 md:p-8">
              <div>
                <div className="text-xs uppercase tracking-widest text-peach">Redo?</div>
                <div className="mt-1 text-lg font-semibold">Skicka ärendet — det tar två minuter.</div>
              </div>
              <a
                href="#kontaktformular"
                className="inline-flex items-center gap-2 rounded-full bg-peach px-6 py-3 text-sm font-semibold text-peach-foreground hover:shadow-[0_14px_40px_-12px] hover:shadow-peach/50"
              >
                Till formuläret →
              </a>
            </div>
          </div>
        </div>
      </section>
      <CallbackBar />
    </div>
  );
}

function ChannelCard({
  icon: Icon,
  label,
  value,
  href,
  note,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  href: string;
  note?: string;
}) {
  return (
    <a
      href={href}
      className="group rounded-2xl border border-white/10 bg-surface/50 p-4 transition-colors hover:border-peach/40"
    >
      <span className="grid h-10 w-10 place-items-center rounded-full bg-peach/10 text-peach">
        <Icon className="h-4 w-4" />
      </span>
      <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-medium group-hover:text-peach">{value}</div>
      {note ? <p className="mt-1 text-xs text-muted-foreground">{note}</p> : null}
    </a>
  );
}
